import { useCallback, useState } from 'react';
import { createRoot, type Root } from 'react-dom/client';

import { Receipt } from '@/components/orders/Receipt';
import { usePrinterStore } from '@/stores/printerStore';
import { isPrinterConnected, printViaBluetooth } from '@/lib/btPrinter';
import { I18nextProvider } from 'react-i18next';
import i18n from '@/i18n';
import type { OrderDTO } from '@/types/api';

/**
 * Print a receipt. Prefers the paired Bluetooth printer if connected;
 * otherwise falls back to `window.print()` with the receipt CSS sheet.
 */
export function usePrintReceipt() {
  const paperWidth = usePrinterStore((s) => s.paperWidth);
  const [pending, setPending] = useState(false);

  const print = useCallback(
    async (order: OrderDTO) => {
      setPending(true);
      try {
        if (isPrinterConnected()) {
          await printViaBluetooth(order, paperWidth);
          return;
        }
        await browserPrint(order, paperWidth);
      } finally {
        setPending(false);
      }
    },
    [paperWidth],
  );

  return { print, pending };
}

/**
 * Renders the Receipt component into a detached <div>, attaches it to
 * `<body>`, sets the paper-width attribute on <html>, and calls window.print().
 * Cleans up afterward.
 */
async function browserPrint(order: OrderDTO, paperWidth: 58 | 80): Promise<void> {
  const host = document.createElement('div');
  host.id = 'receipt-host';
  document.body.appendChild(host);
  document.documentElement.setAttribute('data-paper', String(paperWidth));

  const root: Root = createRoot(host);
  root.render(
    <I18nextProvider i18n={i18n}>
      <Receipt order={order} />
    </I18nextProvider>,
  );

  // Wait a frame so the receipt is in the DOM before printing.
  await new Promise<void>((resolve) => requestAnimationFrame(() => resolve()));

  return new Promise<void>((resolve) => {
    const cleanup = () => {
      root.unmount();
      host.remove();
      document.documentElement.removeAttribute('data-paper');
      window.removeEventListener('afterprint', cleanup);
      resolve();
    };
    window.addEventListener('afterprint', cleanup, { once: true });
    window.print();
  });
}
